import { SPACE_FILE_RAW_UPLOAD_MAX_BYTES, spaceMessageFileReferenceSchema } from "./schemas/http/space-files.js";
import * as z$1 from "zod/v4";
//#region src/space-work-handoff.ts
const SPACE_WORK_HANDOFF_CAPABILITY_HEADER = "x-openmeld-space-work-handoff";
const unsafeSegment = /[<>:"\\|?*\u0000-\u001f]|[. ]$/u;
const reservedSegment = /^(?:con|prn|aux|nul|com[1-9]|lpt[1-9])(?:\.|$)/iu;
const spaceWorkRelativePathSchema = z$1.string().min(1).max(1024).refine((path) => path.split("/").every((part) => part.length > 0 && part !== "." && part !== ".." && part.toLowerCase() !== ".git" && part.toLowerCase() !== ".openmeld-skills" && !unsafeSegment.test(part) && !reservedSegment.test(part)), "Use a relative file path without reserved names, traversal, or unsafe characters.");
const spaceWorkFileSelectionSchema = z$1.strictObject({
	fileId: z$1.string().min(1).max(128),
	revision: z$1.number().int().positive(),
	relativePath: spaceWorkRelativePathSchema
});
const spaceWorkSelectionSchema = z$1.strictObject({
	schemaVersion: z$1.literal(1),
	files: z$1.array(spaceWorkFileSelectionSchema).min(1).max(10)
}).superRefine(({ files }, context) => {
	const paths = /* @__PURE__ */ new Set();
	const ids = /* @__PURE__ */ new Set();
	for (const file of files) {
		const path = file.relativePath.normalize("NFC").toLowerCase();
		if (ids.has(file.fileId) || [...paths].some((other) => other === path || other.startsWith(`${path}/`) || path.startsWith(`${other}/`))) context.addIssue({
			code: "custom",
			message: "Select each file once with distinct, non-overlapping paths."
		});
		ids.add(file.fileId);
		paths.add(path);
	}
});
const SPACE_WORK_MAX_BYTES = 10 * SPACE_FILE_RAW_UPLOAD_MAX_BYTES;
const spaceWorkHandoffSchema = spaceWorkSelectionSchema.safeExtend({
	skills: z$1.array(z$1.strictObject({
		skillId: z$1.string().min(1).max(128),
		releaseId: z$1.string().min(1).max(128),
		digest: z$1.string().regex(/^[a-f0-9]{64}$/u),
		name: z$1.string().min(1).max(128)
	})).max(10).refine((skills) => new Set(skills.map((skill) => skill.skillId)).size === skills.length, "Select each Skill once."),
	work: z$1.strictObject({
		goal: z$1.string().min(1).max(4e3),
		completed: z$1.string().max(4e3),
		nextSteps: z$1.string().min(1).max(4e3)
	}),
	requirements: z$1.array(z$1.string().min(1).max(500)).max(10)
});
/** Service-only projection after preparing the dispatch's selected inputs. */
const spaceWorkingCopyAccessSchema = z$1.object({ workingCopyId: z$1.string().uuid().optional() });
const spaceWorkingCopyResultSchema = z$1.union([
	z$1.strictObject({
		copyId: z$1.string().uuid(),
		root: z$1.string(),
		ready: z$1.literal(true)
	}),
	z$1.strictObject({
		copyId: z$1.string().uuid(),
		reconciled: z$1.literal(true)
	}),
	z$1.strictObject({
		copyId: z$1.string().uuid(),
		refreshed: z$1.literal(true)
	}),
	z$1.strictObject({
		copyId: z$1.string().uuid(),
		resolved: z$1.literal(true)
	}),
	z$1.strictObject({
		copyId: z$1.string().uuid(),
		references: z$1.array(spaceMessageFileReferenceSchema).max(10),
		files: z$1.array(spaceWorkFileSelectionSchema).max(10)
	}),
	z$1.strictObject({
		copyId: z$1.string().uuid(),
		root: z$1.string(),
		files: z$1.array(spaceWorkFileSelectionSchema.extend({
			incomingRevision: z$1.number().int().positive().optional(),
			incomingPath: spaceWorkRelativePathSchema.optional(),
			status: z$1.enum([
				"conflict",
				"pending",
				"preparing",
				"missing",
				"clean",
				"changed"
			])
		})).max(10)
	})
]);
//#endregion
export { SPACE_WORK_HANDOFF_CAPABILITY_HEADER, SPACE_WORK_MAX_BYTES, spaceWorkFileSelectionSchema, spaceWorkHandoffSchema, spaceWorkRelativePathSchema, spaceWorkSelectionSchema, spaceWorkingCopyAccessSchema, spaceWorkingCopyResultSchema };

//# sourceMappingURL=space-work-handoff.js.map